import { Container } from '@mui/material';
import MessageInputField from './MessageInputField';
import MessageList from './MessageList';
import { CaseMessageModel, NewMessageModel } from '../../../lib/models/CaseMessageModel';
import { useGet } from '../../../lib/api/hooks';
import { post } from '../../../lib/api';
import { toast } from 'react-toastify';
import { useScreenSmallerThan } from '../../../lib/hooks/breakpoint-hooks';
import useTrackEvent from '../../../lib/hooks/use-track-event';
import useTrackException from '../../../lib/hooks/use-track-exception';
import { useEffect } from 'react';
import { getEnv } from '../../../lib/helper/get-env';

type CaseMessagesProps = {
    caseId: string | undefined;
    crmClient: string | undefined;
    userId: string | undefined;
    caseStage: string;
    isReadonly: boolean;
    onApiData?: (result: CaseMessageModel[]) => void;
};

const CaseMessages = ({
    caseId,
    crmClient,
    userId,
    caseStage,
    isReadonly,
    onApiData
}: CaseMessagesProps) => {
    const trackEvent = useTrackEvent();
    const trackException = useTrackException();

    const { data, refreshData } = useGet<CaseMessageModel[]>(
        `messaging\\getMessages?crmClient=${crmClient}&caseId=${caseId}&userId=${userId}`
    );

    useEffect(() => {
        data !== null && onApiData && onApiData(data);
    }, [data, onApiData]);

    const isSmallerThanSmall = useScreenSmallerThan('sm');

    const handleSave = async (messageText: string) => {
        const message: NewMessageModel = {
            crmName: crmClient ? crmClient : '',
            crmCaseId: caseId ? caseId : '',
            createdBy: userId ? userId : '',
            text: messageText
        };
        try {
            const result = await post(`messaging/postmessage`, message);
            if (result.data != null) {
                trackEvent('Case message is sent (MDNav)', { sentBy: message.createdBy });
                toast.success('Message sent', { autoClose: 5000 });
                refreshData();
            }
        } catch (error) {
            trackException(error as Error, { sentBy: message.createdBy });
            toast.error('An error occurred while sending the message', { autoClose: 5000 });
            console.log(error);
        }
    };

    useEffect(() => {
        let refreshTimeoutRef: NodeJS.Timeout | undefined;
        getEnv('REACT_APP_REFRESH_TIMEOUT')
            .then((refreshTimeout) => Number(refreshTimeout))
            .then((refreshTimeout) => {
                const interval = setInterval(() => {
                    refreshData();
                }, refreshTimeout * 1000);
                refreshTimeoutRef = interval;
            });
        return () => {
            if (refreshTimeoutRef) clearInterval(refreshTimeoutRef);
        };
    }, [refreshData]);

    return (
        <Container
            maxWidth={false}
            sx={{
                marginBottom: '0.125rem',
                marginTop: isSmallerThanSmall ? '0' : '1.25rem',
                padding: '0 0.813rem'
            }}>
            <MessageInputField
                handleSave={handleSave}
                disabled={isReadonly}
                caseStage={caseStage}
            />
            <MessageList data={data} />
        </Container>
    );
};

export default CaseMessages;
